
import React, { useMemo } from 'react';
import { idbGet, idbSet } from '../utils/cache/persist';
import { useQuery } from '@tanstack/react-query';
import { fetchSqlData, fetchSqlDataWithQuery, fetchSqlDataStreaming } from '../services/api'; // Adjust the path

interface UseGenericFetchResult<T> {
    data?: { data: T[] };
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
    refetch: () => void;
}

// Simple streaming hook: minimal wrapper around fetchSqlDataStreaming with no watchdog/persistence/event refresh
export function useSimpleStream<T = any>(
  queryKey: string,
  enabled: boolean,
  opts: { table: string; orderBy?: StreamOrderBy; columns?: StreamColumns; batchSize?: number; flushMs?: number } & { persist?: boolean; cacheOnce?: boolean; cacheVersion?: string; gcDays?: number; incrementalPersistMs?: number; devRemountGuardMs?: number; flowControl?: boolean }
) {
  const [data, setData] = React.useState<T[]>([]);
  const [version, setVersion] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<Error | null>(null);

  const controllerRef = React.useRef<{ abort: () => void } | null>(null);
  const storeRef = React.useRef<T[]>([]); // single growing buffer kept by ref (no per-commit copying)
  const commitTimerRef = React.useRef<number | null>(null);
  const guardTimerRef = React.useRef<number | null>(null);
  // Adaptive cadence: faster early for first paint, slower as list grows to reduce churn
  const getCommitMs = (len: number) => (len < 10000 ? 150 : len < 100000 ? 240 : 400);
  const lastCommittedLenRef = React.useRef(0);
  const lastPersistAtRef = React.useRef<number>(0);
  const onMessageRef = React.useRef<((e: BusEvent) => void) | null>(null);
  const flowControlRef = React.useRef<boolean>(!!opts.flowControl);
  const cacheKey = React.useMemo(() => {
    const ver = opts.cacheVersion || 'v0';
    const tbl = String(opts.table || '');
    const ob = Array.isArray(opts.orderBy) ? opts.orderBy.join(',') : String(opts.orderBy || '');
    const cols = Array.isArray(opts.columns) ? opts.columns.join(',') : String(opts.columns || '');
    // Note: Do NOT include queryKey so that different hook instances share the same persisted snapshot
    return `stream:${tbl}:${ver}:${ob}:${cols}`;
  }, [opts.cacheVersion, opts.table, opts.orderBy, opts.columns]);

  // Singleflight dedupe: share one in-flight stream per network key (excludes queryKey)
  type BusEvent = { type: 'batch'; rows: any[] } | { type: 'done' } | { type: 'error'; error: any };
  type Bus = { subscribers: Set<(e: BusEvent) => void>; controller: { abort: () => void } | null };
  const simpleBusRegistry = (useSimpleStream as any)._bus as Map<string, Bus> | undefined;
  const BUS: Map<string, Bus> = simpleBusRegistry || new Map<string, Bus>();
  // Attach registry once to the function to keep it module-scoped across hook calls
  if (!(useSimpleStream as any)._bus) (useSimpleStream as any)._bus = BUS;
  const networkKey = React.useMemo(() => {
    const ver = opts.cacheVersion || 'v0';
    const tbl = String(opts.table || '');
    const ob = Array.isArray(opts.orderBy) ? opts.orderBy.join(',') : String(opts.orderBy || '');
    const cols = Array.isArray(opts.columns) ? opts.columns.join(',') : String(opts.columns || '');
    return `net:${tbl}:${ver}:${ob}:${cols}`;
  }, [opts.cacheVersion, opts.table, opts.orderBy, opts.columns]);

  const scheduleCommit = React.useCallback(() => {
    if (commitTimerRef.current != null) return;
    const ms = getCommitMs(storeRef.current.length);
    commitTimerRef.current = window.setTimeout(() => {
      commitTimerRef.current = null;
      const total = storeRef.current.length;
      const last = lastCommittedLenRef.current;
      if (total === last) return;
      // Append only the delta to produce a new reference for consumers
      const delta = (storeRef.current as any[]).slice(last) as T[];
      lastCommittedLenRef.current = total;
      const apply = () => setData(prev => (prev.length === 0 ? delta.slice() : (prev as any[]).concat(delta as any[])) as T[]);
      // Prefer non-blocking update when available
      if ((React as any).startTransition) {
        (React as any).startTransition(apply);
      } else {
        apply();
      }
      // bump version to signal consumers new data committed
      setVersion(v => v + 1);
      // signal worker we're ready for the next batch when flowControl is enabled
      try {
        if (flowControlRef.current) {
          const ctrl = BUS.get(networkKey)?.controller as any;
          ctrl?.ready?.();
        }
      } catch {}
    }, ms);
  }, []);

  const appendRowsInPlace = React.useCallback((rows: any[]) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const target = storeRef.current as any[];
    for (let i = 0; i < rows.length; i += 1) {
      target.push(rows[i]);
    }
  }, []);

  React.useEffect(() => {
    if (!enabled) return;
    let aborted = false;
    const persist = !!opts.persist;
    const cacheOnce = !!opts.cacheOnce;
    const gcDays = typeof opts.gcDays === 'number' ? opts.gcDays : undefined;
    const incrementalMs = typeof opts.incrementalPersistMs === 'number' ? opts.incrementalPersistMs : undefined;
    flowControlRef.current = !!opts.flowControl;

    const hydrate = async () => {
      // Try to hydrate from IDB if enabled
      if (!persist) return { hydrated: false } as const;
      try {
        const raw = await idbGet<any>(cacheKey);
        const payload = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (payload && Array.isArray(payload.data)) {
          if (typeof gcDays === 'number') {
            const age = Date.now() - Number(payload.ts || 0);
            if (age > gcDays * 24 * 60 * 60 * 1000) return { hydrated: false } as const;
          }
          if (aborted) return { hydrated: false } as const;
          setData(payload.data as T[]);
          setVersion(v => v + 1);
          storeRef.current = payload.data as T[];
          lastCommittedLenRef.current = storeRef.current.length;
          return { hydrated: true } as const;
        }
      } catch {}
      return { hydrated: false } as const;
    };

    const start = async () => {
      setData([]);
      setVersion(0);
      storeRef.current = [];
      lastCommittedLenRef.current = 0;
      setError(null);
      setIsLoading(true);

      const h = cacheOnce ? await hydrate() : { hydrated: false } as const;
      if (cacheOnce && h.hydrated) {
        if (process.env.NODE_ENV !== 'production') {
          try { console.debug('[useSimpleStream] hydrated from cache and skipped network', { cacheKey, networkKey }); } catch {}
        }
        // Use cache and skip network
        setIsLoading(false);
        return;
      }

      // Subscribe to shared bus; start network only if not already running
      const bus = (BUS.get(networkKey) as Bus) || { subscribers: new Set(), controller: null };
      BUS.set(networkKey, bus);
      const onMessage = (evt: BusEvent) => {
        if (evt.type === 'batch') {
          // Avoid spread with very large arrays (can cause call stack RangeError)
          appendRowsInPlace(evt.rows as any[]);
          scheduleCommit();
          if (persist && incrementalMs) {
            const now = Date.now();
            if (now - lastPersistAtRef.current >= incrementalMs) {
              lastPersistAtRef.current = now;
              try { idbSet(cacheKey, JSON.stringify({ ts: now, data: storeRef.current })); } catch {}
            }
          }
        } else if (evt.type === 'done') {
          const total = storeRef.current.length;
          const last = lastCommittedLenRef.current;
          if (total !== last) {
            const delta = (storeRef.current as any[]).slice(last) as T[];
            lastCommittedLenRef.current = total;
            const apply = () => setData(prev => (prev.length === 0 ? delta.slice() : (prev as any[]).concat(delta as any[])) as T[]);
            if ((React as any).startTransition) (React as any).startTransition(apply); else apply();
            setVersion(v => v + 1);
          }
          if (persist) {
            try { idbSet(cacheKey, JSON.stringify({ ts: Date.now(), data: storeRef.current })); } catch {}
          }
          setIsLoading(false);
        } else if (evt.type === 'error') {
          const e = evt.error;
          setError(e instanceof Error ? e : new Error(String(e)));
          setIsLoading(false);
        }
      };
      onMessageRef.current = onMessage;
      bus.subscribers.add(onMessage);

      if (process.env.NODE_ENV !== 'production') {
        try { console.debug('[useSimpleStream] subscribing to bus', { networkKey, hadController: !!bus.controller }); } catch {}
      }
      const startNetwork = () => {
        if (BUS.get(networkKey)?.controller) return; // someone else started in the meantime
        const controller = fetchSqlDataStreaming({
          table: opts.table,
          orderBy: opts.orderBy,
          columns: opts.columns,
          batchSize: opts.batchSize,
          flushMs: opts.flushMs,
          flowControl: !!opts.flowControl,
          onBatch: (rows: any[]) => {
            // Broadcast rows to all subscribers (async) to avoid deep call stacks
            const b = BUS.get(networkKey);
            if (!b) return;
            const snapshot = Array.from(b.subscribers);
            queueMicrotask(() => {
              snapshot.forEach(fn => fn({ type: 'batch', rows } as BusEvent));
            });
          },
          onDone: () => {
            const b = BUS.get(networkKey);
            if (!b) return;
            const snapshot = Array.from(b.subscribers);
            queueMicrotask(() => {
              snapshot.forEach(fn => fn({ type: 'done' } as BusEvent));
            });
          },
          onError: (e: any) => {
            const b = BUS.get(networkKey);
            if (!b) return;
            const snapshot = Array.from(b.subscribers);
            queueMicrotask(() => {
              snapshot.forEach(fn => fn({ type: 'error', error: e } as BusEvent));
            });
          },
        });
        const b = BUS.get(networkKey);
        if (b) b.controller = controller;
        if (process.env.NODE_ENV !== 'production') {
          try { console.debug('[useSimpleStream] started network stream', { networkKey }); } catch {}
        }
        // Kick off first token for flow-controlled streams
        try { (controller as any)?.ready?.(); } catch {}
      };

      if (!bus.controller) {
        const guardMs = (process.env.NODE_ENV !== 'production' && typeof opts.devRemountGuardMs === 'number' && opts.devRemountGuardMs > 0)
          ? opts.devRemountGuardMs
          : 0;
        if (guardMs > 0) {
          if (guardTimerRef.current != null) window.clearTimeout(guardTimerRef.current);
          guardTimerRef.current = window.setTimeout(() => {
            guardTimerRef.current = null;
            startNetwork();
          }, guardMs);
        } else {
          startNetwork();
        }
      }
      controllerRef.current = BUS.get(networkKey)?.controller || null;
      // If subscribing to an already-running flow-controlled stream, issue a token to keep it flowing
      try {
        if (flowControlRef.current) (controllerRef.current as any)?.ready?.();
      } catch {}
    };

    start();
    return () => {
      // Unsubscribe from bus; if no subscribers remain, abort the shared controller to free the connection
      const bus = BUS.get(networkKey);
      if (bus && onMessageRef.current) {
        bus.subscribers.delete(onMessageRef.current);
        onMessageRef.current = null;
        if (bus.subscribers.size === 0 && bus.controller) {
          if (process.env.NODE_ENV !== 'production') {
            try { console.debug('[useSimpleStream] aborting shared controller (last subscriber removed)', { networkKey }); } catch {}
          }
          try { bus.controller.abort(); } catch {}
          bus.controller = null;
        }
      }
      controllerRef.current = null;
      if (commitTimerRef.current != null) {
        window.clearTimeout(commitTimerRef.current);
        commitTimerRef.current = null;
      }
      if (guardTimerRef.current != null) {
        window.clearTimeout(guardTimerRef.current);
        guardTimerRef.current = null;
      }
      aborted = true;
    };
  }, [enabled, queryKey, opts.table, opts.orderBy, opts.columns, opts.batchSize, opts.flushMs, scheduleCommit]);

  const abort = React.useCallback(() => {
    const c = controllerRef.current;
    if (!c) return;
    try { c.abort(); } catch {}
    controllerRef.current = null;
  }, []);

  const refetch = React.useCallback(() => {
    // toggling enabled from caller is recommended; here we simply abort and let effect restart on deps change
    abort();
    setData([]);
    setVersion(0);
    storeRef.current = [];
    lastCommittedLenRef.current = 0;
    setError(null);
    setIsLoading(true);
    const controller = fetchSqlDataStreaming({
      table: opts.table,
      orderBy: opts.orderBy,
      columns: opts.columns,
      batchSize: opts.batchSize,
      flushMs: opts.flushMs,
      onBatch: (rows: any[]) => {
        // Same safe append as in main stream handler
        appendRowsInPlace(rows as any[]);
        scheduleCommit();
      },
      onDone: () => {
        const total = storeRef.current.length;
        const last = lastCommittedLenRef.current;
        if (total !== last) {
          const delta = (storeRef.current as any[]).slice(last) as T[];
          lastCommittedLenRef.current = total;
          setData(prev => (prev.length === 0 ? delta.slice() : (prev as any[]).concat(delta as any[])) as T[]);
        }
        setIsLoading(false);
      },
      onError: (e: any) => {
        setError(e instanceof Error ? e : new Error(String(e)));
        setIsLoading(false);
      },
    });
    controllerRef.current = controller;
  }, [abort, opts.table, opts.orderBy, opts.columns, opts.batchSize, opts.flushMs, scheduleCommit, appendRowsInPlace]);

  return { data, version, isLoading, isError: !!error, error, refetch, abort } as const;
}

// Backward-compat export name
export { useGenericStreamFetch_old as useGenericStreamFetch };

type CacheOptions = {
    persist?: boolean;
    cacheOnce?: boolean;
    cacheVersion?: string;
    gcDays?: number;
};

export const useGenericFetch = <T>( 
    tableName: string,
    queryKey: string,
    staleTime: number = 5 * 60 * 1000, // 5 minutes default
    database?: string | null,
    enableRefetch = false,
    enableEventRefresh = false,
    eventName?: string,
    cacheOptions?: CacheOptions,
    enabledAtEnd: boolean = true,
    retryOverride?: number,
    refetchOnFocusOverride?: boolean,
    refetchOnReconnectOverride?: boolean,
    networkModeOverride?: 'online' | 'always' | 'offlineFirst',
    refetchOnMountOverride?: boolean | 'always'
): UseGenericFetchResult<T> => {
    const { persist, cacheOnce, cacheVersion, gcDays } = cacheOptions || {};
    const queryFn = React.useCallback(async () => {
        try {
            return await fetchSqlData(tableName, undefined, undefined, database);
        } catch (error) {
            console.error(`Error fetching ${tableName}:`, error);
            throw error;
        }
    }, [tableName, database]);

    const { data, isLoading, isError, error, refetch } = useQuery<T[], Error>({
        queryKey: [queryKey, { database, v: cacheVersion }],
        queryFn,
        staleTime: cacheOnce ? Infinity : staleTime,
        gcTime: gcDays ? gcDays * 24 * 60 * 60 * 1000 : undefined,
        retry: typeof retryOverride === 'number' ? retryOverride : 2,
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
        refetchOnWindowFocus: typeof refetchOnFocusOverride === 'boolean' ? refetchOnFocusOverride : (enableRefetch ? true : false),
        refetchOnMount: typeof refetchOnMountOverride !== 'undefined' ? refetchOnMountOverride : false,
        refetchOnReconnect: typeof refetchOnReconnectOverride === 'boolean' ? refetchOnReconnectOverride : (enableRefetch ? true : false),
        enabled: enabledAtEnd,
        networkMode: networkModeOverride,
        meta: { persist: !!persist },
    });

    // Add event listener for manual refresh if enabled
    React.useEffect(() => {
        if (!enableEventRefresh) return;
        
        const eventToListen = eventName || `${tableName}:changed`;
        const handleRefresh = () => {
            refetch();
        };

        window.addEventListener(eventToListen, handleRefresh);
        return () => {
            window.removeEventListener(eventToListen, handleRefresh);
        };
    }, [enableEventRefresh, eventName, refetch, tableName]);

    // Memoize the wrapper so referential identity is stable across renders
    const wrapped = useMemo(() => (data ? { data } : undefined), [data]);

    return {
        data: wrapped,
        isLoading,
        isError,
        error: error || null,
        refetch,
    };
};



type StreamOrderBy = string | string[] | undefined;
type StreamColumns = string[] | undefined;

type StreamSnapshot<T> = { data: T[]; isLoading: boolean; error: Error | null };
type StreamState<T> = {
  snap: StreamSnapshot<T>;
  controller: { abort: () => void } | null;
  subscribers: Set<(s: StreamSnapshot<T>) => void>;
};

const activeStreams = new Map<string, StreamState<any>>();

export function useGenericStreamFetch_old<T = any>(
  tableName: string,
  queryKey: string,
  enabled: boolean = false,
  options?: {
    orderBy?: StreamOrderBy;
    columns?: StreamColumns;
    batchSize?: number;
    flushMs?: number;
    eventRefresh?: boolean;
    eventName?: string;
    stallMs?: number;
  },
  cacheOptions?: CacheOptions
) {
  const [data, setData] = React.useState<T[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<Error | null>(null);
  const hydratedAtRef = React.useRef<number | null>(null);
  const cacheKey = React.useMemo(() => {
    const ver = cacheOptions?.cacheVersion || 'v0';
    return `stream:${queryKey}:${ver}`;
  }, [queryKey, cacheOptions?.cacheVersion]);
  const streamKey = React.useMemo(() => {
    const o = {
      tableName,
      orderBy: options?.orderBy,
      columns: options?.columns,
      batchSize: options?.batchSize,
      flushMs: options?.flushMs,
      ver: cacheOptions?.cacheVersion || 'v0',
    };
    try { return `k:${JSON.stringify(o)}`; } catch { return `k:${tableName}:${String(options?.orderBy)}:${String(options?.columns)}`; }
  }, [tableName, options?.orderBy, options?.columns, options?.batchSize, options?.flushMs, cacheOptions?.cacheVersion]);
  const gcMs = React.useMemo(() => (cacheOptions?.gcDays ? cacheOptions.gcDays * 24 * 60 * 60 * 1000 : undefined), [cacheOptions?.gcDays]);
  const havePersist = !!cacheOptions?.persist;

  // Try to hydrate from cache on mount/key change
  React.useEffect(() => {
    if (!havePersist) return;
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { ts: number; data: T[] };
      if (parsed && Array.isArray(parsed.data)) {
        if (typeof gcMs === 'number') {
          const age = Date.now() - Number(parsed.ts || 0);
          if (age > gcMs) return; // expired
        }
        setData(parsed.data as any);
      }
    } catch {}
  }, [cacheKey, havePersist, gcMs]);

  const startSingleton = React.useCallback(() => {
    let state = activeStreams.get(streamKey) as StreamState<T> | undefined;
    if (state && state.snap.isLoading) return; // already running
    // If cacheOnce and we already have cached data, skip starting
    if (havePersist && cacheOptions?.cacheOnce) {
      try {
        const raw = localStorage.getItem(cacheKey);
        if (raw) {
          const parsed = JSON.parse(raw) as { ts: number; data: T[] };
          if (parsed && Array.isArray(parsed.data)) {
            if (typeof gcMs !== 'number' || (Date.now() - Number(parsed.ts || 0)) <= gcMs) {
              // Seed local state; do not start worker
              setData(parsed.data as any);
              setIsLoading(false);
              return;
            }
          }
        }
      } catch {}
    }
    const initial: StreamState<T> = state || { snap: { data: [], isLoading: true, error: null }, controller: null, subscribers: new Set() };
    initial.snap = { data: [], isLoading: true, error: null };
    activeStreams.set(streamKey, initial);
    const notify = () => {
      initial.subscribers.forEach((fn) => fn(initial.snap));
    };
    const stallMs = typeof options?.stallMs === 'number' ? options.stallMs : 30000;
    const persistThrottleMs = 2000;
    let lastPersist = 0;
    let lastTick = Date.now();
    const tick = () => { lastTick = Date.now(); };
    const watchdog = window.setInterval(() => {
      if (!initial || !initial.snap.isLoading) return;
      if (Date.now() - lastTick > stallMs) {
        try { initial.controller?.abort(); } catch {}
        initial.controller = null;
        initial.snap = { data: [], isLoading: false, error: null };
        window.clearInterval(watchdog);
        // restart
        startSingleton();
      }
    }, Math.min(Math.max((stallMs / 3) | 0, 3000), 10000));
    initial.controller = fetchSqlDataStreaming({
      table: tableName,
      orderBy: options?.orderBy,
      columns: options?.columns,
      batchSize: options?.batchSize,
      flushMs: options?.flushMs,
      onBatch: (rows: any[]) => {
        initial.snap = { ...initial.snap, data: (initial.snap.data as any[]).concat(rows as any) };
        tick();
        // Throttled incremental persist to allow hydration after refresh mid-stream
        if (havePersist) {
          const now = Date.now();
          if (now - lastPersist >= persistThrottleMs) {
            try {
              const payload = JSON.stringify({ ts: now, data: initial.snap.data });
              localStorage.setItem(cacheKey, payload);
            } catch {}
            lastPersist = now;
          }
        }
        notify();
      },
      onDone: () => {
        initial.snap = { ...initial.snap, isLoading: false };
        if (havePersist) {
          try {
            const payload = JSON.stringify({ ts: Date.now(), data: initial.snap.data });
            localStorage.setItem(cacheKey, payload);
          } catch {}
        }
        window.clearInterval(watchdog);
        notify();
      },
      onError: (e: any) => {
        const err = e instanceof Error ? e : new Error(String(e));
        initial.snap = { ...initial.snap, isLoading: false, error: err };
        window.clearInterval(watchdog);
        notify();
      },
    });
  }, [streamKey, tableName, options?.orderBy, options?.columns, options?.batchSize, options?.flushMs, havePersist, cacheOptions?.cacheOnce, cacheKey, gcMs]);

  React.useEffect(() => {
    if (!enabled) return;
    // Subscribe to singleton
    const state = (activeStreams.get(streamKey) as StreamState<T> | undefined) || { snap: { data: [], isLoading: false, error: null }, controller: null, subscribers: new Set() };
    activeStreams.set(streamKey, state);
    // If we have fresh cache and cacheOnce, hydrate the shared snapshot to avoid starting a network stream
    if (havePersist && cacheOptions?.cacheOnce && (!state.snap.data || state.snap.data.length === 0)) {
      try {
        const raw = localStorage.getItem(cacheKey);
        if (raw) {
          const parsed = JSON.parse(raw) as { ts: number; data: T[] };
          if (parsed && Array.isArray(parsed.data)) {
            if (typeof gcMs !== 'number' || (Date.now() - Number(parsed.ts || 0)) <= gcMs) {
              state.snap = { data: parsed.data as any, isLoading: false, error: null };
              hydratedAtRef.current = Date.now();
            }
          }
        }
      } catch {}
    }
    const handler = (snap: StreamSnapshot<T>) => {
      setData(snap.data);
      setIsLoading(snap.isLoading);
      setError(snap.error);
    };
    state.subscribers.add(handler);
    // Push current snapshot immediately
    handler(state.snap);
    // Start if not running
    if (!state.snap.isLoading && (!state.snap.data || state.snap.data.length === 0)) {
      startSingleton();
    } else if (state.snap.isLoading) {
      // already running — just wait for updates
    }
    return () => {
      state.subscribers.delete(handler);
      if (state.subscribers.size === 0 && state.controller) {
        try { state.controller.abort(); } catch {}
        // Keep last snapshot for hydration; do not delete immediately
        state.controller = null;
      }
    };
  }, [enabled, streamKey, startSingleton]);

  const abort = React.useCallback(() => {
    const state = activeStreams.get(streamKey);
    if (!state) return;
    try { state.controller?.abort(); } catch {}
    state.controller = null;
  }, [streamKey]);

  const refetch = React.useCallback(() => {
    if (!enabled) return;
    const state = activeStreams.get(streamKey) as StreamState<T> | undefined;
    if (state && state.controller) {
      try { state.controller.abort(); } catch {}
      state.controller = null;
    }
    // Reset snapshot and start
    if (state) {
      state.snap = { data: [], isLoading: false, error: null };
    }
    startSingleton();
  }, [enabled, streamKey, startSingleton]);

  // Optional event-based refresh
  React.useEffect(() => {
    if (!options?.eventRefresh) return;
    const ev = options?.eventName || `${tableName}:changed`;
    const h = () => {
      const t = hydratedAtRef.current;
      if (t && Date.now() - t < 2000) return; // ignore early events right after cache hydration
      refetch();
    };
    window.addEventListener(ev, h);
    return () => window.removeEventListener(ev, h);
  }, [options?.eventRefresh, options?.eventName, tableName, refetch]);

  return {
    data,
    isLoading,
    isError: !!error,
    error,
    refetch,
    abort,
  } as const;
}

/**
 * Fetches data from a database table with a simple WHERE clause filter
 * @param tableName - Name of the database table to query
 * @param column - Column name to filter on
 * @param value - Value to filter by (must be a primitive value)
 * @param options - Additional react-query options
 * @returns Query result with typed data array
 */
// Cache for storing active queries to prevent duplicate connections
const activeQueries = new Map<string, Promise<any>>();

// Function to safely execute a query with retry logic
const executeWithRetry = async <T>(queryFn: () => Promise<T>, maxRetries = 3, baseDelay = 1000): Promise<T> => {
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const result = await queryFn();
      return result;
    } catch (error: any) {
      lastError = error;
      
      // If it's a connection error, wait and retry
      if (error.message?.includes('too many clients') || error.message?.includes('connection')) {
        const delay = baseDelay * Math.pow(2, attempt);
        console.warn(`Connection error (attempt ${attempt + 1}/${maxRetries}), retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        // For other errors, rethrow immediately
        throw error;
      }
    }
  }
  
  // If we've exhausted all retries, throw the last error
  throw lastError;
};

export function useFilteredData<T = any>(
  tableName: string,
  column: string,
  value: string | number | boolean | null | undefined,
  options: Record<string, any> = {}
) {
  const queryFn = React.useCallback(async () => {
    if (value === undefined || value === null) return [];

    const queryKey = JSON.stringify({ tableName, column, value });
    
    // If we already have an active query for this key, return its promise
    if (activeQueries.has(queryKey)) {
      return activeQueries.get(queryKey);
    }

    const queryPromise = (async () => {
      try {
        // Simple sanitization - in production, use proper parameterized queries
        const safeTableName = tableName.replace(/[^a-zA-Z0-9_]/g, '');
        const safeColumn = column.replace(/[^a-zA-Z0-9_]/g, '');
        
        // Get database from options or use null for default
        const database = options.database || null;
        
        // Add a small delay to prevent connection storms
        await new Promise(resolve => setTimeout(resolve, 50));
        
        const data = await executeWithRetry(
          () => fetchSqlData(safeTableName, safeColumn, value, database),
          2, // Reduced max retries
          1000 // base delay
        );
        
        // Ensure we return an array even if data is undefined or null
        const result = Array.isArray(data) ? data : [];
        return result;
      } catch (error) {
        console.error(`Error fetching filtered data from ${tableName}:`, error);
        // Don't cache failed requests
        activeQueries.delete(queryKey);
        throw error;
      }
    })();
    
    // Store the promise for this query
    activeQueries.set(queryKey, queryPromise);
    
    // Clean up the query when it's done
    queryPromise.finally(() => {
      setTimeout(() => activeQueries.delete(queryKey), 1000); // Small delay before cleanup
    });
    
    return queryPromise;
  }, [tableName, column, value, options.database]);

  return useQuery<T[]>({
    queryKey: ['filteredData', tableName, column, value],
    queryFn,
    retry: 2, // Reduced retry count
    retryDelay: attemptIndex => Math.min(1000 * Math.pow(2, attemptIndex), 10000), // Max 10s delay
    refetchOnWindowFocus: false, // Disabled by default, can be overridden in options
    refetchOnMount: false, // Disabled by default, can be overridden in options
    staleTime: 5 * 60 * 1000, // 5 minutes default
    gcTime: 10 * 60 * 1000, // 10 minutes cache time
    ...options
  });
}